"use client";

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  Volume2,
  VolumeOff,
  Sparkles,
  X,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { useAtom } from "jotai";
import { isPausedAtom } from "@/atoms/player";
import { format } from "date-fns";
import Link from "next/link";
import { Marquee } from "./ui/marquee";
import { CircularProgress } from "./ui/circular-progress";
import { cn } from "@/lib/utils";
import Image from "next/image";

interface AudioPlayerProps {
  current: {
    title: string;
    cover: string;
    date: string;
    audio: string;
    id: string;
  };
  audioRef: React.RefObject<HTMLAudioElement | null>;
  isProUser?: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onClose?: () => void;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  current,
  audioRef,
  isProUser = false,
  isExpanded,
  onToggleExpanded,
  onClose,
}) => {
  const [isPaused, setIsPaused] = useAtom(isPausedAtom);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Initialize duration and current time if audio is already loaded
    if (audio.duration && !isNaN(audio.duration)) {
      setDuration(audio.duration);
      setCurrentTime(audio.currentTime);
      const prog = (audio.currentTime / audio.duration) * 100;
      setProgress(prog);
    }

    if ("mediaSession" in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: current.title,
        artist: format(new Date(current.date), "MMMM dd, yyyy"),
        artwork: [
          {
            src: current.cover || "https://via.placeholder.com/512",
            sizes: "512x512",
            type: "image/jpeg",
          },
        ],
      });

      if (!isNaN(audio.duration) && isFinite(audio.duration)) {
        try {
          navigator.mediaSession.setPositionState({
            duration: audio.duration,
            playbackRate: audio.playbackRate,
            position: audio.currentTime,
          });
        } catch (e) {
          console.warn("Failed to set position state:", e);
        }
      }
    }

    const handlePlay = () => setIsPaused(false);
    const handlePause = () => setIsPaused(true);
    const handleVolumeChange = () => setIsMuted(audio.muted);
    const handleTimeUpdate = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        const prog = (audio.currentTime / audio.duration) * 100;
        setProgress(prog);
        setCurrentTime(audio.currentTime);
      }
    };
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      if (audio.currentTime) {
        setCurrentTime(audio.currentTime);
        const prog = (audio.currentTime / audio.duration) * 100;
        setProgress(prog);
      }
    };
    const handleDurationChange = () => {
      setDuration(audio.duration);
    };

    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("volumechange", handleVolumeChange);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("durationchange", handleDurationChange);

    return () => {
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("volumechange", handleVolumeChange);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("durationchange", handleDurationChange);
    };
  }, [current, setIsPaused, audioRef]);

  const handlePlaybackRate = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playbackRate === 0.5) {
      setPlaybackRate(1);
      audio.playbackRate = 1;
    } else if (playbackRate === 1) {
      setPlaybackRate(1.5);
      audio.playbackRate = 1.5;
    } else if (playbackRate === 1.5) {
      setPlaybackRate(2);
      audio.playbackRate = 2;
    } else if (playbackRate === 2) {
      setPlaybackRate(0.5);
      audio.playbackRate = 0.5;
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPaused) {
      audio.pause();
    } else {
      audio.play();
    }
  }, [isPaused, audioRef]);

  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Collapsed pill view
  if (!isExpanded) {
    return (
      <Button
        variant="outline"
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpanded();
        }}
        className="shadow-sm px-3 py-2 h-auto rounded-full flex items-center gap-2 bg-card/90 before:absolute before:inset-0 before:-z-10 before:backdrop-blur-[6px] before:rounded-full border relative min-w-0 max-w-[200px]"
      >
        <div className="flex-1 min-w-0 overflow-hidden">
          <Marquee speed={20} fade={true} className="text-sm">
            <span className="whitespace-nowrap px-2">{current.title}</span>
          </Marquee>
        </div>

        <CircularProgress
          progress={progress}
          size={20}
          strokeWidth={2}
          className="flex-shrink-0"
        />
      </Button>
    );
  }

  // Expanded view - use a simpler approach without MediaController
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 10, scale: 0.95 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="w-full max-w-md bg-card/90 before:absolute before:inset-0 before:-z-10 before:backdrop-blur-[6px] before:rounded-2xl rounded-2xl border shadow-sm p-4 relative"
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex gap-3 flex-1 min-w-0">
          <Link
            href={`/bite/${current.id}`}
            className="flex-shrink-0 hover:scale-105 transition-transform"
          >
            <img
              src={current.cover}
              alt={current.title}
              width={64}
              height={48}
              className="rounded-sm object-cover aspect-[4/3] mt-1 mr-0.5"
            />
          </Link>
          <div className="flex flex-col min-w-0 flex-1">
            <Link
              href={`/bite/${current.id}`}
              className="text-sm font-semibold line-clamp-2 hover:underline hover:decoration-primary/50"
            >
              {current.title}
            </Link>
            <span className="text-xs text-muted-foreground">
              {format(new Date(current.date), "MMMM dd, yyyy")}
            </span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpanded();
          }}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Scrubber */}
      <input
        type="range"
        min="0"
        max={duration || 0}
        value={currentTime}
        step="0.01"
        onChange={(e) => {
          const audio = audioRef.current;
          if (audio) {
            audio.currentTime = parseFloat(e.target.value);
          }
        }}
        className="audio-scrubber w-full mb-2 rounded-full appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, hsl(var(--primary)) ${duration ? (currentTime / duration) * 100 : 0}%, hsl(var(--muted)) ${duration ? (currentTime / duration) * 100 : 0}%)`,
        }}
      />

      <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>

      <TooltipProvider>
        <div className="w-full h-auto px-0 flex items-center justify-center gap-x-3">
          <Tooltip>
            <TooltipTrigger asChild>
              {isProUser ? (
                <Button
                  className="size-8 rounded-full hover:scale-105 text-muted-foreground text-xs font-medium"
                  variant="ghost"
                  onClick={handlePlaybackRate}
                  size="sm"
                >
                  {playbackRate === 0.5 ? (
                    <span>0.5x</span>
                  ) : playbackRate === 1 ? (
                    <span>1x</span>
                  ) : playbackRate === 1.5 ? (
                    <span>1.5x</span>
                  ) : playbackRate === 2 ? (
                    <span>2x</span>
                  ) : null}
                </Button>
              ) : (
                <Button
                  className="size-8 rounded-full hover:scale-105 text-muted-foreground"
                  variant="ghost"
                  size="sm"
                  asChild
                >
                  <Link href="/plan">
                    <Sparkles className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              )}
            </TooltipTrigger>
            <TooltipContent>Playback speed</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="size-8 rounded-full hover:scale-105 hover:-rotate-[30deg]"
                variant="ghost"
                size="sm"
                onClick={() => {
                  const audio = audioRef.current;
                  if (audio) {
                    audio.currentTime = Math.max(0, audio.currentTime - 10);
                  }
                }}
              >
                <RotateCcw className="h-4 w-4 text-muted-foreground" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Rewind 10s</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="size-9 rounded-full hover:scale-105"
                onClick={() => setIsPaused(!isPaused)}
              >
                {isPaused ? (
                  <Play className="fill-background text-background h-4 w-4" />
                ) : (
                  <Pause className="fill-background text-background h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isPaused ? "Play" : "Pause"}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="size-8 rounded-full hover:scale-105 hover:rotate-[30deg]"
                variant="ghost"
                size="sm"
                onClick={() => {
                  const audio = audioRef.current;
                  if (audio) {
                    audio.currentTime = Math.min(
                      audio.duration,
                      audio.currentTime + 30
                    );
                  }
                }}
              >
                <RotateCw className="h-4 w-4 text-muted-foreground" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Skip 30s</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="size-8 rounded-full hover:scale-105"
                variant="ghost"
                size="sm"
                onClick={() => {
                  const audio = audioRef.current;
                  if (audio) {
                    audio.muted = !audio.muted;
                    setIsMuted(audio.muted);
                  }
                }}
              >
                {isMuted ? (
                  <VolumeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Volume2 className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isMuted ? "Unmute" : "Mute"}</TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    </motion.div>
  );
};

export default AudioPlayer;
